import { create } from 'zustand';
import { supabase } from './supabase';
import { User } from '@supabase/supabase-js';
import { handleSupabaseError } from './supabase';
import type { Database } from './database.types';

type Profile = Database['public']['Tables']['profiles']['Row'];
type Medication = Database['public']['Tables']['medications']['Row'];
type Reminder = Database['public']['Tables']['reminders']['Row'];
type MoodLog = Database['public']['Tables']['mood_logs']['Row'];

interface AuthState {
  user: User | null;
  profile: any | null;
  setUser: (user: User | null) => void;
  setProfile: (profile: any | null) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (data: { email: string; password: string; captchaToken: string }) => Promise<void>;
  signOut: () => Promise<void>;
  loadProfile: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  profile: null,
  setUser: (user) => set({ user }),
  setProfile: (profile) => set({ profile }),
  
  signIn: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) throw new Error(handleSupabaseError(error));
    set({ user: data.user });
    await get().loadProfile();
  },

  signUp: async ({ email, password, captchaToken }) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/login`,
        data: {
          email
        },
        captchaToken
      }
    });

    if (error) throw new Error(handleSupabaseError(error));
    if (!data.user) throw new Error('Registration failed');

    // Create initial profile
    const { error: profileError } = await supabase
      .from('profiles')
      .insert([{ id: data.user.id, email: data.user.email }]);

    if (profileError) throw new Error(handleSupabaseError(profileError));
    
    set({ user: data.user });
    await get().loadProfile();
  },
  
  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw new Error(handleSupabaseError(error));
    set({ user: null, profile: null });
  },
  
  loadProfile: async () => {
    const { user } = get();
    if (!user) return;
    
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();
      
    if (error) throw new Error(handleSupabaseError(error));
    set({ profile: data });
  },

  resetPassword: async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    
    if (error) throw new Error(handleSupabaseError(error));
  },
}));

interface MedicationState {
  medications: Medication[];
  reminders: Reminder[];
  loadMedications: () => Promise<void>;
  loadReminders: () => Promise<void>;
  addMedication: (medication: Omit<Medication, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateMedication: (id: string, medication: Partial<Medication>) => Promise<void>;
  deleteMedication: (id: string) => Promise<void>;
  addReminder: (reminder: Omit<Reminder, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateReminder: (id: string, reminder: Partial<Reminder>) => Promise<void>;
  deleteReminder: (id: string) => Promise<void>;
}

export const useMedicationStore = create<MedicationState>((set, get) => ({
  medications: [],
  reminders: [],
  
  loadMedications: async () => {
    const { data, error } = await supabase
      .from('medications')
      .select('*')
      .order('created_at', { ascending: false });
      
    if (error) throw new Error(handleSupabaseError(error));
    set({ medications: data });
  },
  
  loadReminders: async () => {
    const { data, error } = await supabase
      .from('reminders')
      .select('*, medications(*)')
      .order('reminder_time', { ascending: true });
      
    if (error) throw new Error(handleSupabaseError(error));
    set({ reminders: data });
  },
  
  addMedication: async (medication) => {
    const { data, error } = await supabase
      .from('medications')
      .insert(medication)
      .select()
      .single();
      
    if (error) throw new Error(handleSupabaseError(error));
    const medications = get().medications;
    set({ medications: [data, ...medications] });
  },
  
  updateMedication: async (id, medication) => {
    const { data, error } = await supabase
      .from('medications')
      .update(medication)
      .eq('id', id)
      .select()
      .single();
      
    if (error) throw new Error(handleSupabaseError(error));
    const medications = get().medications.map((m) => 
      m.id === id ? data : m
    );
    set({ medications });
  },

  deleteMedication: async (id) => {
    const { error } = await supabase
      .from('medications')
      .delete()
      .eq('id', id);
      
    if (error) throw new Error(handleSupabaseError(error));
    const medications = get().medications.filter((m) => m.id !== id);
    set({ medications });
  },
  
  addReminder: async (reminder) => {
    const { data, error } = await supabase
      .from('reminders')
      .insert(reminder)
      .select()
      .single();
      
    if (error) throw new Error(handleSupabaseError(error));
    const reminders = get().reminders;
    set({ reminders: [data, ...reminders] });
  },
  
  updateReminder: async (id, reminder) => {
    const { data, error } = await supabase
      .from('reminders')
      .update(reminder)
      .eq('id', id)
      .select()
      .single();
      
    if (error) throw new Error(handleSupabaseError(error));
    const reminders = get().reminders.map((r) => 
      r.id === id ? data : r
    );
    set({ reminders });
  },

  deleteReminder: async (id) => {
    const { error } = await supabase
      .from('reminders')
      .delete()
      .eq('id', id);
      
    if (error) throw new Error(handleSupabaseError(error));
    const reminders = get().reminders.filter((r) => r.id !== id);
    set({ reminders });
  },
}));

interface MoodState {
  moodLogs: MoodLog[];
  loadMoodLogs: () => Promise<void>;
  addMoodLog: (log: Omit<MoodLog, 'id' | 'created_at'>) => Promise<void>;
}

export const useMoodStore = create<MoodState>((set, get) => ({
  moodLogs: [],
  
  loadMoodLogs: async () => {
    const { data, error } = await supabase
      .from('mood_logs')
      .select('*')
      .order('created_at', { ascending: false });
      
    if (error) throw new Error(handleSupabaseError(error));
    set({ moodLogs: data });
  },
  
  addMoodLog: async (log) => {
    const { data, error } = await supabase
      .from('mood_logs')
      .insert(log)
      .select()
      .single();
      
    if (error) throw new Error(handleSupabaseError(error));
    const moodLogs = get().moodLogs;
    set({ moodLogs: [data, ...moodLogs] });
  },
}));